/**
 * Quién va encima de quién entre las dos capas fijas de importación.
 *
 * ⚠️⚠️ MEDIDO POR LA UI el 2026-08-13 con un test de impacto real
 * (`document.elementFromPoint` sobre el centro de cada botón), viewport
 * 1280×720 — un portátil corriente:
 *
 *     botón «Importar operaciones»   x 1041–1248 · y 684–720
 *     panel de progreso              x  880–1264 · y 526–704
 *     elementFromPoint(centro)  →  el PANEL, no el botón
 *
 * El panel de progreso se queda en pantalla DESPUÉS de importar (mientras haya
 * importaciones completadas sin descartar) y aparcaba encima de la barra de
 * acciones del importador personalizado: «Cancelar», «Guardar Plantilla» e
 * «Importar operaciones» —los tres— dejaban de responder al clic. Estaban
 * visibles y habilitados, así que ni un barrido de controles deshabilitados ni
 * uno de nombres accesibles lo veían.
 *
 * El panel se monta en `AuthenticatedLayout`, es GLOBAL y no puede saber que
 * una página concreta tiene barra pegada abajo. Por eso quien se defiende es la
 * barra: es local, sabe que existe, y su contenido son las acciones que la
 * página promete.
 *
 * Las dos constantes viven JUNTAS a propósito: separadas eran dos números
 * sueltos en dos ficheros y su relación no la comprobaba nadie.
 */

/** Panel flotante de progreso de importación (`ImportProgressIndicator`). */
export const IMPORT_PROGRESS_PANEL_Z_INDEX = 50

/**
 * Barra de acciones del importador personalizado. Por encima del panel: si el
 * panel tapa parte de su propia lista es un incordio; si tapa el botón que
 * ejecuta la importación, la página no funciona.
 */
export const CUSTOM_IMPORTER_ACTION_BAR_Z_INDEX = 60

/**
 * Clases Tailwind, escritas como LITERALES a propósito.
 *
 * ⚠️ Tailwind genera CSS escaneando el TEXTO del código: un
 * `z-[${constante}]` construido en ejecución no lo ve, no emite la regla, y
 * las dos capas se quedarían sin z-index — peor que antes y sin avisar. El
 * test hermano comprueba que estos literales siguen cuadrando con los números
 * de arriba.
 */
export const IMPORT_PROGRESS_PANEL_Z_CLASS = 'z-[50]'
export const CUSTOM_IMPORTER_ACTION_BAR_Z_CLASS = 'z-[60]'

/**
 * ⚠️⚠️ EL AGUJERO GEMELO QUE ABRIÓ EL ARREGLO DE ARRIBA. Medido por la UI el
 * 2026-08-15 con la misma sonda (`document.elementFromPoint`), viewport
 * 1280×720, en `/import/custom` → «Guardar Plantilla»:
 *
 *     botón «Guardar Plantilla» DEL DIÁLOGO   y 617–665
 *     barra de acciones (z-60)                y 605–676
 *     elementFromPoint(centro)  →  la BARRA, no el botón
 *
 * Los diálogos de la app son el `Modal` compartido, que vive en z-50: al subir
 * la barra por encima del panel de progreso quedó también por encima de ELLOS,
 * y el cliente no podía guardar la plantilla que acababa de configurar. En toda
 * la app sólo esta barra pasa de z-50 (los toasts van a 9999), así que la
 * excepción es suya y suya es la cesión.
 *
 * Mientras haya un diálogo abierto la barra baja POR DEBAJO de los dos: da
 * igual que el panel de progreso la tape, porque en ese momento el usuario está
 * en el diálogo. Se entera por `useIsAnyModalOpen()`, que lee la pila real de
 * modales — no por adivinar clases del `<body>`.
 */
export const CUSTOM_IMPORTER_ACTION_BAR_BEHIND_DIALOG_Z_CLASS = 'z-40'

/**
 * Clase que el panel pone en `<body>` mientras se muestra. Con ella el CSS
 * (`body.import-progress-panel-visible` en `index.css`) reserva 208 px abajo.
 *
 * ⚠️⚠️ Medido por la UI el 2026-08-13 tras importar y navegar a
 * «Transacciones» POR LOS ENLACES de la app (una recarga dura mata el panel, y
 * por eso un barrido con `goto` daba verde sobre un conjunto vacío): el panel
 * tapaba **la paginación del libro** — «Primera página», «Anterior»,
 * «Siguiente» y «Última página»—, de modo que quien acababa de importar no
 * podía recorrer sus propias transacciones.
 *
 * Subir la capa no vale aquí: esos controles van en el flujo, no pegados al
 * viewport. Lo que hace falta es que la página pueda desplazarse un poco más
 * para sacarlos de debajo. El panel mide 178 px y se separa 16 px del borde:
 * 208 deja margen sin dejar un hueco visible cuando no hay panel.
 */
export const IMPORT_PROGRESS_PANEL_BODY_CLASS = 'import-progress-panel-visible'
